import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { getTodayChallengeGame, isDailyChallengeComplete } from '../utils/xp';
import { useLanguage } from '../i18n/LanguageContext';
import './Games.css';
import ProfileMenu from '../ProfileMenu';

const TEXT = {
    title: { en: 'Games', it: 'Giochi', es: 'Juegos', fr: 'Jeux', pt: 'Jogos' },
    subtitle: { en: 'Practice through play', it: 'Esercitati giocando', es: 'Practica jugando', fr: 'Pratiquez en jouant', pt: 'Pratique brincando' },
    xpWeek: { en: 'Your XP this week', it: 'I tuoi XP di questa settimana', es: 'Tus XP de esta semana', fr: 'Vos XP cette semaine', pt: 'Seus XP desta semana' },
    xpHint: {
        en: "Play Word Search or Word Wheel to earn XP. Words come from your Word Bank and today's level.",
        it: 'Gioca a Cerca parole o alla Ruota delle parole per guadagnare XP. Le parole vengono dal tuo Word Bank e dal livello di oggi.',
        es: 'Juega a la Sopa de letras o a la Rueda de palabras para ganar XP. Las palabras salen de tu Word Bank y del nivel de hoy.',
        fr: "Jouez aux Mots mêlés ou à la Roue des mots pour gagner des XP. Les mots viennent de votre Word Bank et du niveau du jour.",
        pt: 'Jogue Caça-palavras ou Roda de palavras para ganhar XP. As palavras vêm do seu Word Bank e do nível de hoje.',
    },
    dailyTitle: { en: 'Daily challenge', it: 'Sfida del giorno', es: 'Reto del día', fr: 'Défi du jour', pt: 'Desafio do dia' },
    dailyDone: {
        en: 'Completed today. Come back tomorrow!',
        it: 'Completata oggi. Torna domani!',
        es: 'Completado hoy. ¡Vuelve mañana!',
        fr: 'Terminé aujourd’hui. Revenez demain !',
        pt: 'Concluído hoje. Volte amanhã!',
    },
    bonus: { en: '+50 XP bonus', it: '+50 XP di bonus', es: '+50 XP de bonificación', fr: '+50 XP de bonus', pt: '+50 XP de bônus' },
    play: { en: 'Play →', it: 'Gioca →', es: 'Jugar →', fr: 'Jouer →', pt: 'Jogar →' },
    filterAll: { en: 'All games', it: 'Tutti i giochi', es: 'Todos los juegos', fr: 'Tous les jeux', pt: 'Todos os jogos' },
    filterQuick: { en: 'Quick (under 5 min)', it: 'Veloci (meno di 5 min)', es: 'Rápidos (menos de 5 min)', fr: 'Rapides (moins de 5 min)', pt: 'Rápidos (menos de 5 min)' },
    filterVocab: { en: 'Vocabulary', it: 'Vocabolario', es: 'Vocabulario', fr: 'Vocabulaire', pt: 'Vocabulário' },
    filterSpelling: { en: 'Spelling', it: 'Ortografia', es: 'Ortografía', fr: 'Orthographe', pt: 'Ortografia' },
    sectionAll: { en: 'All games', it: 'Tutti i giochi', es: 'Todos los juegos', fr: 'Tous les jeux', pt: 'Todos os jogos' },
    top: { en: "This week's top players", it: 'I migliori della settimana', es: 'Los mejores de la semana', fr: 'Les meilleurs de la semaine', pt: 'Os melhores da semana' },
    lbEmpty: {
        en: 'No XP earned yet this week. Be the first!',
        it: 'Nessun XP guadagnato questa settimana. Sii il primo!',
        es: 'Aún no hay XP esta semana. ¡Sé el primero!',
        fr: 'Aucun XP gagné cette semaine. Soyez le premier !',
        pt: 'Ainda não há XP esta semana. Seja o primeiro!',
    },
    learner: { en: 'Learner', it: 'Studente', es: 'Estudiante', fr: 'Apprenant', pt: 'Aluno' },
    new: { en: 'NEW', it: 'NUOVO', es: 'NUEVO', fr: 'NOUVEAU', pt: 'NOVO' },
    easy: { en: 'Easy', it: 'Facile', es: 'Fácil', fr: 'Facile', pt: 'Fácil' },
    medium: { en: 'Medium', it: 'Medio', es: 'Medio', fr: 'Moyen', pt: 'Médio' },
    hard: { en: 'Hard', it: 'Difficile', es: 'Difícil', fr: 'Difficile', pt: 'Difícil' },
    navHome: { en: 'Home', it: 'Home', es: 'Inicio', fr: 'Accueil', pt: 'Início' },
    navTopics: { en: 'Topics', it: 'Argomenti', es: 'Temas', fr: 'Thèmes', pt: 'Temas' },
    navGrammar: { en: 'Grammar', it: 'Grammatica', es: 'Gramática', fr: 'Grammaire', pt: 'Gramática' },
    navWordbank: { en: 'Word bank', it: 'Word bank', es: 'Word bank', fr: 'Word bank', pt: 'Word bank' },
    navGames: { en: 'Games', it: 'Giochi', es: 'Juegos', fr: 'Jeux', pt: 'Jogos' },
};

const GAME_TEXT = {
    1: {
        name: { en: 'Word search', it: 'Cerca parole', es: 'Sopa de letras', fr: 'Mots mêlés', pt: 'Caça-palavras' },
        desc: {
            en: 'Find hidden words in the grid',
            it: 'Trova le parole nascoste nella griglia',
            es: 'Encuentra las palabras ocultas en la cuadrícula',
            fr: 'Trouvez les mots cachés dans la grille',
            pt: 'Encontre as palavras escondidas na grade',
        },
    },
    2: {
        name: { en: 'Word wheel', it: 'Ruota delle parole', es: 'Rueda de palabras', fr: 'Roue des mots', pt: 'Roda de palavras' },
        desc: {
            en: 'Form as many words as you can from the letters',
            it: 'Forma più parole possibili con le lettere',
            es: 'Forma todas las palabras que puedas con las letras',
            fr: 'Formez le plus de mots possible avec les lettres',
            pt: 'Forme o maior número de palavras com as letras',
        },
    },
    3: {
        name: { en: 'Crossword', it: 'Cruciverba', es: 'Crucigrama', fr: 'Mots croisés', pt: 'Palavras cruzadas' },
        desc: {
            en: 'Clues in your language, answers in the language you are learning',
            it: 'Indizi nella tua lingua, risposte nella lingua che stai imparando',
            es: 'Pistas en tu idioma, respuestas en el idioma que aprendes',
            fr: 'Indices dans votre langue, réponses dans la langue que vous apprenez',
            pt: 'Dicas no seu idioma, respostas no idioma que você está aprendendo',
        },
    },
    4: {
        name: { en: 'Flashcard sprint', it: 'Sprint di flashcard', es: 'Sprint de tarjetas', fr: 'Sprint de cartes', pt: 'Sprint de flashcards' },
        desc: {
            en: 'Tap the correct translation before time runs out',
            it: 'Tocca la traduzione giusta prima che scada il tempo',
            es: 'Toca la traducción correcta antes de que se acabe el tiempo',
            fr: 'Touchez la bonne traduction avant la fin du temps',
            pt: 'Toque na tradução certa antes que o tempo acabe',
        },
    },
    5: {
        name: { en: 'Anagram', it: 'Anagramma', es: 'Anagrama', fr: 'Anagramme', pt: 'Anagrama' },
        desc: {
            en: 'Unscramble the word before the timer runs out',
            it: 'Rimetti in ordine la parola prima che scada il tempo',
            es: 'Ordena la palabra antes de que se acabe el tiempo',
            fr: 'Remettez le mot en ordre avant la fin du temps',
            pt: 'Desembaralhe a palavra antes que o tempo acabe',
        },
    },
    6: {
        name: { en: 'Translation sprint', it: 'Sprint di traduzione', es: 'Sprint de traducción', fr: 'Sprint de traduction', pt: 'Sprint de tradução' },
        desc: {
            en: 'Words flash fast. Tap the right translation quickly',
            it: 'Le parole scorrono veloci. Tocca subito la traduzione giusta',
            es: 'Las palabras pasan rápido. Toca enseguida la traducción correcta',
            fr: 'Les mots défilent vite. Touchez vite la bonne traduction',
            pt: 'As palavras passam rápido. Toque logo na tradução certa',
        },
    },
};

const CHALLENGES = {
    'word-search': { id: 1 },
    'word-wheel': { id: 2 },
    'flashcard-sprint': { id: 4 },
};

const LANG_NAMES = {
    italian: { en: 'Italian', it: 'Italiano', es: 'Italiano', fr: 'Italien', pt: 'Italiano' },
    spanish: { en: 'Spanish', it: 'Spagnolo', es: 'Español', fr: 'Espagnol', pt: 'Espanhol' },
    french: { en: 'French', it: 'Francese', es: 'Francés', fr: 'Français', pt: 'Francês' },
    english: { en: 'English', it: 'Inglese', es: 'Inglés', fr: 'Anglais', pt: 'Inglês' },
    portuguese: { en: 'Portuguese', it: 'Portoghese', es: 'Portugués', fr: 'Portugais', pt: 'Português' },
};

const LANG_FLAGS = { italian: '🇮🇹', spanish: '🇪🇸', french: '🇫🇷', english: '🇬🇧', portuguese: '🇧🇷' };

const games = [
    { id: 1, emoji: '🔍', xp: 30, difficulty: 'easy', color: '#E1F5EE', border: '#9FE1CB' },
    { id: 2, emoji: '🔤', xp: 25, difficulty: 'medium', color: '#FAEEDA', border: '#FAC775' },
    { id: 3, emoji: '✏️', xp: 40, difficulty: 'hard', color: '#EEEDFE', border: '#CECBF6' },
    { id: 4, emoji: '🃏', xp: 20, difficulty: 'easy', color: '#E1F5EE', border: '#9FE1CB' },
    { id: 5, emoji: '🔀', xp: 20, difficulty: 'medium', color: '#FAECE7', border: '#F5C4B3' },
    { id: 6, emoji: '⚡', xp: 30, difficulty: 'hard', color: '#E6F1FB', border: '#B5D4F4' },
];

function Games() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const gameName = (id) => GAME_TEXT[id].name[uiLang] || GAME_TEXT[id].name.en;
    const gameDesc = (id) => GAME_TEXT[id].desc[uiLang] || GAME_TEXT[id].desc.en;
    const [activeFilter, setActiveFilter] = useState('all');
    const [displayName, setDisplayName] = useState(localStorage.getItem('vozoly_display_name') || 'U');
    const userInitial = displayName[0].toUpperCase();
    const [leaderboard, setLeaderboard] = useState([]);
    const [myXp, setMyXp] = useState(0);
    const [challengeGame, setChallengeGame] = useState('word-search');
    const [challengeDone, setChallengeDone] = useState(false);
    const [wordMixLoading, setWordMixLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setChallengeGame(getTodayChallengeGame());
            const done = await isDailyChallengeComplete();
            setChallengeDone(done);

            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('display_name')
                    .eq('id', user.id)
                    .single();
                const realName = profileData?.display_name || user.email?.split('@')[0];
                if (realName) {
                    setDisplayName(realName);
                    localStorage.setItem('vozoly_display_name', realName);
                    const { data: existingStats } = await supabase
                        .from('user_stats')
                        .select('total_xp')
                        .eq('user_id', user.id)
                        .maybeSingle();
                    await supabase.from('user_stats').upsert({
                        user_id: user.id,
                        display_name: realName,
                        total_xp: existingStats?.total_xp || 0,
                    }, { onConflict: 'user_id' });
                }
            }

            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
            const rankingLanguage = (localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
            const { data: xpRows } = await supabase
                .from('xp_log')
                .select('user_id, amount')
                .eq('target_language', rankingLanguage)
                .gte('created_at', weekAgo);

            if (xpRows) {
                const totals = {};
                xpRows.forEach(r => { totals[r.user_id] = (totals[r.user_id] || 0) + r.amount; });
                const userIds = Object.keys(totals);
                if (userIds.length > 0) {
                    const { data: names } = await supabase
                        .from('user_stats')
                        .select('user_id, display_name')
                        .in('user_id', userIds);
                    const nameMap = {};
                    (names || []).forEach(n => { nameMap[n.user_id] = n.display_name || ''; });
                    const ranked = userIds
                        .map(id => ({ id, xp: totals[id], name: nameMap[id] || '' }))
                        .sort((a, b) => b.xp - a.xp)
                        .slice(0, 5);
                    setLeaderboard(ranked);
                    if (user) {
                        const mine = ranked.find(r => r.id === user.id);
                        setMyXp(mine ? mine.xp : (totals[user.id] || 0));
                    }
                }
            }
            setWordMixLoading(false);
        };
        load();
    }, []);

    const filters = [
        { id: 'all', label: tx('filterAll') },
        { id: 'easy', label: tx('filterQuick') },
        { id: 'vocabulary', label: tx('filterVocab') },
        { id: 'spelling', label: tx('filterSpelling') },
    ];

    const targetLang = (localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const langFlag = LANG_FLAGS[targetLang] || '';
    const langName = LANG_NAMES[targetLang] ? (LANG_NAMES[targetLang][uiLang] || LANG_NAMES[targetLang].en) : targetLang;

    const filteredGames = games.filter(game => {
        if (activeFilter === 'all') return true;
        if (activeFilter === 'easy') return game.difficulty === 'easy';
        if (activeFilter === 'vocabulary') return [4, 6].includes(game.id);
        if (activeFilter === 'spelling') return [5, 2, 3].includes(game.id);
        return true;
    });

    const challengeId = CHALLENGES[challengeGame] ? CHALLENGES[challengeGame].id : 1;

    return (
        <div className="games-screen">

            <div className="games-top-bar">
                <div className="games-top-row">
                    <div>
                        <div className="games-title">{tx('title')}</div>
                        <div className="games-subtitle">{tx('subtitle')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="games-lang-pill">
                            {langFlag} {langName}
                        </div>
                        <ProfileMenu initial={userInitial} />
                    </div>
                </div>

                <div className="games-word-mix">
                    <div className="games-mix-label">{tx('xpWeek')}</div>
                    <div className="games-mix-xp-value">{wordMixLoading ? '...' : myXp} XP</div>
                    <div className="games-mix-legend">
                        <div className="games-mix-item">
                            {tx('xpHint')}
                        </div>
                    </div>
                </div>
            </div>

            <div className="games-daily" onClick={() => !challengeDone && navigate(`/games/${challengeGame}`)} style={{ cursor: challengeDone ? 'default' : 'pointer' }}>
                <div className="games-daily-icon">{challengeDone ? '✅' : '🏆'}</div>
                <div className="games-daily-info">
                    <div className="games-daily-title">
                        {tx('dailyTitle')}: {gameName(challengeId)}
                    </div>
                    <div className="games-daily-desc">
                        {challengeDone ? tx('dailyDone') : `${gameDesc(challengeId)} · ${tx('bonus')}`}
                    </div>
                </div>
                {!challengeDone && <div className="games-daily-timer">{tx('play')}</div>}
            </div>

            <div className="games-filters">
                {filters.map(f => (
                    <button
                        key={f.id}
                        className={`games-filter-btn ${activeFilter === f.id ? 'active' : ''}`}
                        onClick={() => setActiveFilter(f.id)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="games-section-header">
                <div className="games-section-label">{tx('sectionAll')}</div>
            </div>

            <div className="games-grid">
                {filteredGames.map(game => (
                    <div
                        key={game.id}
                        className="games-card"
                        onClick={() => {
                            if (game.id === 1) navigate('/games/word-search');
                            if (game.id === 2) navigate('/games/word-wheel');
                            if (game.id === 3) navigate('/games/crossword');
                            if (game.id === 4) navigate('/games/flashcard-sprint');
                            if (game.id === 5) navigate('/games/anagram');
                            if (game.id === 6) navigate('/games/translation-sprint');
                        }}
                        style={{ cursor: [1, 2, 3, 4, 5, 6].includes(game.id) ? 'pointer' : 'default' }}
                    >
                        <div
                            className="games-card-visual"
                            style={{ background: game.color }}
                        >
                            <div className="games-card-emoji">{game.emoji}</div>
                            {game.isNew && <div className="games-card-new">{tx('new')}</div>}
                        </div>
                        <div className="games-card-info">
                            <div className="games-card-name">{gameName(game.id)}</div>
                            <div className="games-card-desc">{gameDesc(game.id)}</div>
                            <div className="games-card-footer">
                                <div className="games-card-xp">+{game.xp} XP</div>
                                <div className="games-card-difficulty">{tx(game.difficulty)}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="games-section-header" style={{ marginTop: '8px' }}>
                <div className="games-section-label">{tx('top')}</div>
            </div>

            <div className="games-leaderboard">
                {leaderboard.length === 0 ? (
                    <div className="games-lb-empty">{tx('lbEmpty')}</div>
                ) : (
                    leaderboard.map((row, i) => {
                        const posClass = i === 0 ? 'gold' : i === 1 ? 'silver' : '';
                        const colors = [
                            { bg: '#FAC775', text: '#633806' },
                            { bg: '#B5D4F4', text: '#0C447C' },
                            { bg: '#9FE1CB', text: '#085041' },
                            { bg: '#CECBF6', text: '#3C3489' },
                            { bg: '#F5C4B3', text: '#8C3A1D' },
                        ];
                        const c = colors[i % colors.length];
                        return (
                            <div key={row.id} className="games-lb-row">
                                <div className={`games-lb-pos ${posClass}`}>{i + 1}</div>
                                <div className="games-lb-avatar" style={{ background: c.bg, color: c.text }}>{(row.name || tx('learner'))[0].toUpperCase()}</div>
                                <div className="games-lb-name">{row.name || tx('learner')}</div>
                                <div className="games-lb-xp">{row.xp} XP</div>
                            </div>
                        );
                    })
                )}
            </div>

            <div style={{ height: '80px' }}></div>

            <div className="games-bottom-nav">
                <button className="games-nav-item" onClick={() => navigate('/home')}>
                    <div className="games-nav-icon">🏠</div>
                    <div className="games-nav-label">{tx('navHome')}</div>
                </button>
                <button className="games-nav-item" onClick={() => navigate('/topics')}>
                    <div className="games-nav-icon">📚</div>
                    <div className="games-nav-label">{tx('navTopics')}</div>
                </button>
                <button className="games-nav-item" onClick={() => navigate('/grammar')}>
                    <div className="games-nav-icon nav-icon-grammar">📐</div>
                    <div className="games-nav-label">{tx('navGrammar')}</div>
                </button>
                <button className="games-nav-item" onClick={() => navigate('/wordbank')}>
                    <div className="games-nav-icon">📒</div>
                    <div className="games-nav-label">{tx('navWordbank')}</div>
                </button>
                <button className="games-nav-item active">
                    <div className="games-nav-icon">🎮</div>
                    <div className="games-nav-label">{tx('navGames')}</div>
                    <div className="games-nav-dot"></div>
                </button>
            </div>

        </div>
    );
}

export default Games;